import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { searchAPI } from '../../services/api';
import SearchBox from '../../components/SearchBox/SearchBox';
import ResourceCard from '../../components/ResourceCard/ResourceCard';
import styles from './SearchResults.module.css';

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [diskSources, setDiskSources] = useState([]);

  const query = searchParams.get('q') || '';
  const tagsParam = searchParams.get('tags') || '';
  const diskSourceParam = searchParams.get('diskSource') || '';
  const tags = useMemo(() => tagsParam ? [tagsParam] : null, [tagsParam]);
  const [searchTrigger, setSearchTrigger] = useState(0);

  useEffect(() => {
    fetchDiskSources();
  }, []);

  const fetchDiskSources = async () => {
    try {
      const response = await searchAPI.getDiskSources();
      setDiskSources(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch disk sources:', error);
    }
  };

  const handleSearch = useCallback(async () => {
    if (!query.trim() && !tags) return;

    setLoading(true);
    setError(null);

    try {
      const response = await searchAPI.search(
        query || null,
        currentPage,
        pageSize,
        'created_at',
        'desc',
        tags,
        diskSourceParam || null
      );
      const { data } = response.data;
      console.log('response:', data)

      setResults(prev => currentPage === 1 ? (data.records || []) : [...prev, ...(data.records || [])]);
      setTotal(data.total || 0);
      setTotalPages(data.pages || 0);
    } catch (err) {
      setError('搜索失败，请稍后重试');
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }, [query, tags, currentPage, pageSize, diskSourceParam]);

  useEffect(() => {
    if (query || tags) {
      setCurrentPage(1);
    }
  }, [query, tagsParam, diskSourceParam]);

  useEffect(() => {
    if ((query || tags) && currentPage === 1) {
      handleSearch();
    }
  }, [query, tagsParam, diskSourceParam, tags, handleSearch, searchTrigger, currentPage]);

  useEffect(() => {
    if ((query || tags) && currentPage > 1) {
      handleSearch();
    }
  }, [currentPage, pageSize, query, tags, handleSearch]);

  const handleDiskSourceChange = (source) => {
    const newParams = new URLSearchParams(searchParams);
    if (source) {
      newParams.set('diskSource', source);
    } else {
      newParams.delete('diskSource');
    }
    setSearchParams(newParams);
  };

  const observer = useRef();
  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && currentPage < totalPages) {
        setCurrentPage(prev => prev + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, currentPage, totalPages]);

  const getSearchDisplayText = () => {
    if (query && tags) {
      return `关键词: "${query}" · 标签: "${tags[0]}"`;
    } else if (query) {
      return `关键词: "${query}"`;
    } else if (tags) {
      return `标签: "${tags[0]}"`;
    }
    return '';
  };

  return (
    <>
      <Helmet>
        <title>{query ? `${query} - 搜索结果 | 万盘搜` : tags ? `${tags[0]} - 标签搜索 | 万盘搜` : '搜索结果 | 万盘搜'}</title>
        <meta name="description" content={`在万盘搜中查找"${query || tags?.[0] || ''}"相关的网盘资源，共找到${total}个结果。支持百度网盘、阿里云盘、夸克网盘等多种网盘资源。`} />
        <meta name="keywords" content={`${query || tags?.[0] || ''},网盘搜索,网盘资源,资源下载`} />
        <link rel="canonical" href={`https://wpansou.aicosmos.cn/search?q=${encodeURIComponent(query || '')}`} />
      </Helmet>
      <div className={styles.searchResults}>
      <div className="container">
        <div className={styles.searchHeader}>
          <div className={styles.diskSourceTabs}>
            <button
              className={`${styles.diskSourceTab} ${diskSourceParam === '' ? styles.active : ''}`}
              onClick={() => handleDiskSourceChange('')}
            >
              全部网盘
            </button>
            {diskSources.map((source) => (
              <button
                key={source.source}
                className={`${styles.diskSourceTab} ${diskSourceParam === source.source ? styles.active : ''}`}
                onClick={() => handleDiskSourceChange(source.source)}
              >
                {source.label}
              </button>
            ))}
          </div>
          <SearchBox
            placeholder="搜索网盘资源..."
            className={styles.searchResultsBox}
            diskSource={diskSourceParam}
            defaultValue={query}
            onSearch={() => setSearchTrigger(prev => prev + 1)}
          />
        </div>

        {(query || tags) && (
          <div className={styles.searchInfo}>
            <h2>搜索结果</h2>
            <p>{getSearchDisplayText()}</p>
            {!loading && total > 0 && (
              <p>找到 {total} 个相关结果</p>
            )}
          </div>
        )}

        <div className={styles.searchContent}>
          {loading && currentPage === 1 && (
            <div className={styles.loading}>
              <div className={styles.loadingSpinner}></div>
              <span>搜索中...</span>
            </div>
          )}

          {error && (
            <div className={styles.error}>
              <p>{error}</p>
              <button onClick={handleSearch} className="btn btn-primary">
                重试
              </button>
            </div>
          )}

          {!loading && !error && results.length === 0 && (query || tags) && (
            <div className={styles.noResults}>
              <div className={styles.noResultsIcon}>🔍</div>
              <h3>没有找到相关结果</h3>
              <p>尝试使用不同的关键词或减少搜索条件</p>
            </div>
          )}

          {(!error && results.length > 0) && (
            <>
              <div className={styles.resultsList}>
                {results.map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
              </div>

              <div ref={lastElementRef} className={styles.loadMoreContainer}>
                {loading && currentPage > 1 && (
                  <div className={styles.loadingMore}>
                    <div className={styles.loadingSpinner}></div>
                    <span>加载更多中...</span>
                  </div>
                )}
                {!loading && currentPage >= totalPages && results.length > 0 && (
                  <div className={styles.noMore}>
                    没有更多结果了
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SearchResults;